import AsyncStorage from '@react-native-async-storage/async-storage';
import {
  CompaniesModel,
  CustomizationsModel,
  GDPRModel,
  NDAModel,
  VisitFlowTypesModel,
} from '@w3lcome/types';
import appConfig from '_/config/app';
import { useUpdateCustomization } from '_/hooks/useUpdateCustomization';
import {
  companyApi,
  customizationApi,
  ipadGdpr,
  ipadNdaApi,
  visitFlowTypesApi,
} from '_/services/api';
import logger from '_/services/logger';
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
// import * as Sentry from '@sentry/react-native';

import { changeTranslation } from '_/services/locale';
import { useAuth } from './AuthContext';
import { useLanguage } from './LanguageContext';
import { useUpdateVisitFlowTypes } from './useUpdatevisitFlowTypes';

export interface CustomizationContextData {
  company: CompaniesModel & {liberadoAuthToken?: boolean} | null;
  customization: CustomizationsModel | null;
  gdpr: null | GDPRModel;
  nda: null | NDAModel;
  isVideoPlaying: boolean;
  loadData: () => Promise<void>;
  setIsVideoPlaying(value: boolean): void;
  visitFlowTypes: VisitFlowTypesModel[];
  setSelectedVisitFlowType(value: VisitFlowTypesModel): void;
  selectedVisitFlowType: VisitFlowTypesModel | null;
}

const CustomizationContext = createContext<CustomizationContextData>(
  {} as CustomizationContextData
);

type CustomizationType = {
  children: React.ReactNode;
};

export const CustomizationProvider: React.FC<CustomizationType> = ({ children }) => {
  const { signout, feathersApp, token } = useAuth();
  const [company, setCompany] = useState<CompaniesModel | null>(null);
  const [customization, setCustomization] = useState<CustomizationsModel | null>(null);
  const [visitFlowTypes, setVisitFlowTypes] = useState<VisitFlowTypesModel[]>([]);
  const { languages, changeLanguage, language, getIpadLanguages } = useLanguage();
  const [gdprList, setGdprList] = useState<GDPRModel[]>([]);
  const [ndaList, setNdaList] = useState<NDAModel[]>([]);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [selectedVisitFlowType, setSelectedVisitFlowType] = useState<VisitFlowTypesModel | null>(
    null
  );

  const { updateFields } = useUpdateCustomization();

  const { updateFieldsVisitFlowTypes } = useUpdateVisitFlowTypes();

  useEffect(() => {
    feathersApp?.service('customizations').on('patched', loadData);
    feathersApp?.service('visits-config').on('patched', loadCompany);
    feathersApp?.service('custom-fields').on('patched', loadData);
    feathersApp?.service('custom-fields').on('created', loadData);
    feathersApp?.service('custom-fields').on('removed', loadData);
    feathersApp?.service('ipads').on('removed', () => removeData);

    return () => {
      feathersApp?.service('customizations').off('patched', loadData);
      feathersApp?.service('visits-config').off('patched', loadCompany);
      feathersApp?.service('custom-fields').off('patched', loadData);
      feathersApp?.service('custom-fields').off('created', loadData);
      feathersApp?.service('custom-fields').off('removed', loadData);
      feathersApp?.service('ipads').off('removed', () => removeData);
    };
  }, [feathersApp]);

  const country = useMemo(() => {
    return company?.country?.toLocaleLowerCase();
  }, [company]);

  const gdpr = useMemo(() => {
    if (gdprList.length !== 0) {
      const findCurrentLang = gdprList.find((e) => e.lang === language.code);
      const findLangEn = gdprList.find((e) => e.lang === 'en');

      if (findCurrentLang) {
        return findCurrentLang;
      }

      if (findLangEn) {
        return findLangEn;
      }

      return gdprList[0];
    }

    return null;
  }, [gdprList, language]);

  const nda = useMemo(() => {
    if (ndaList.length !== 0 && selectedVisitFlowType) {
      const selectedNDAs = ndaList.filter(
        (nda) => nda.visitFlowTypeId === selectedVisitFlowType.id
      );

      const findCurrentLang = selectedNDAs.find((e) => e.lang === language.code);
      const findLangEn = selectedNDAs.find((e) => e.lang === 'en');

      if (findCurrentLang) {
        return findCurrentLang;
      }

      if (findLangEn) {
        return findLangEn;
      }

      return selectedNDAs[0];
    }

    return null;
  }, [ndaList, language, selectedVisitFlowType]);

  useEffect(() => {
    async function defaultLanguage() {
      const storedLanguage = await AsyncStorage.getItem(appConfig.languageKey);
      if (!storedLanguage && country) {
        if (country === 'brazil') {
          changeLanguage(languages[2]);
        } else {
          changeLanguage(languages[0]);
        }
      }
    }
    defaultLanguage();
  }, [country]);

  // useEffect(() => {
  //   const user = company ? { id: company.id, username: company.name } : null;

  //   // Sentry.setUser(user);
  // }, [company]);

  function removeData() {
    setCompany(null);
    setCustomization(null);
  }

  async function loadData() {
    try {
      const [companyData, customizationData] = await loadCompanyAndCustomization();

      if (!companyData || !customizationData) return signout();

      const visitFlowTypesData = await loadVisitFlowTypes(companyData.id);
      // TODO: DATABASE LOCAL DESATIVADA
      // const hasCompanyChanged = await checkCompanyChange(companyData.id);

      // if (hasCompanyChanged) await resetDatabase();

      await cacheCompanyAndCustomization(companyData, customizationData, visitFlowTypesData);
      updateAppState(companyData, customizationData, visitFlowTypesData);

      await loadNdaGdprData(
        customizationData.id,
        visitFlowTypesData.map((e: any) => e.id)
      );
    } catch (error) {
      handleLoadError(error);
    }
  }

  async function loadCompanyAndCustomization(): Promise<[CompaniesModel, CustomizationsModel]> {
    const [{ data: companyDataList }, { data: customizationDataList }] = await Promise.all([
      companyApi.getList(),
      customizationApi.getList(),
    ]);
    return [companyDataList[0], updateFields(customizationDataList[0])];
  }

  async function loadVisitFlowTypes(companyId: string) {
    const { data: visitFlowTypesDataList } = await visitFlowTypesApi.getList({
      companyId,
      isEnabled: true,
      $sort: { sort: 1 },
    });

    return updateFieldsVisitFlowTypes(visitFlowTypesDataList);
  }

  async function loadCompany() {
    const { data } = await companyApi.getList();
    const companyData = data[0];

    await AsyncStorage.setItem(appConfig.companyKey, JSON.stringify(companyData));
    setCompany(companyData);
  }

  async function cacheCompanyAndCustomization(
    companyData: CompaniesModel,
    customizationData: CustomizationsModel,
    visitFlowTypesData: VisitFlowTypesModel[]
  ) {
    try {
      await Promise.all([
        AsyncStorage.setItem(appConfig.companyKey, JSON.stringify(companyData)),
        AsyncStorage.setItem(appConfig.customizationKey, JSON.stringify(customizationData)),
        // AsyncStorage.setItem(appConfig.visitFlowTypesKey, JSON.stringify(visitFlowTypesData)),
      ]);

      changeTranslation({
        translationId: customizationData.translationId,
      });
    } catch (error: any) {
      if (error?.name === 'QuotaExceededError') {
        console.warn('Storage cheio — limpando dados antigos…');

        await AsyncStorage.removeItem(appConfig.visitFlowTypesKey);

        try {
          await AsyncStorage.setItem(
            appConfig.visitFlowTypesKey,
            JSON.stringify(visitFlowTypesData)
          );
        } catch (err) {
          console.error('Falhou de novo ao salvar visitFlowTypes:', err);
        }
      } else {
        console.error('Erro inesperado ao salvar cache:', error);
      }
    }
  }

  function updateAppState(
    companyData: CompaniesModel,
    customizationData: CustomizationsModel,
    visitFlowTypesData: any
  ) {
    setCompany(companyData);
    setCustomization(customizationData);
    setVisitFlowTypes(visitFlowTypesData);
  }

  async function loadNdaGdprData(customizationId: string, visitFlowTypesIds: string[]) {
    const [{ data: gdprData }, { data: ndaData }] = await Promise.all([
      ipadGdpr.getList({ customizationId }),
      ipadNdaApi.getList({ 'visitFlowTypeId[$in]': visitFlowTypesIds }),
    ]);

    setGdprList(gdprData);
    getIpadLanguages(customizationId);
    setNdaList(ndaData);
  }

  async function handleLoadError(error: any) {
    logger(error);
    if (error?.response?.status === 401) return signout();

    const [storedCompany, storedCustomization] = await Promise.all([
      AsyncStorage.getItem(appConfig.companyKey),
      AsyncStorage.getItem(appConfig.customizationKey),
      // AsyncStorage.getItem(appConfig.visitFlowTypesKey),
    ]);

    if (storedCompany && storedCustomization) {
      setCompany(JSON.parse(storedCompany));
      setCustomization(JSON.parse(storedCustomization));
      // setVisitFlowTypes(JSON.parse(storedVisitFlowTypes));
    } else {
      signout();
    }
  }

  useEffect(() => {
    if (token) {
      loadData();
    }
  }, [token]);

  return (
    <CustomizationContext.Provider
      value={{
        company,
        customization,
        gdpr,
        nda,
        isVideoPlaying,
        loadData,
        setIsVideoPlaying,
        visitFlowTypes,
        selectedVisitFlowType,
        setSelectedVisitFlowType,
      }}
    >
      {children}
    </CustomizationContext.Provider>
  );
};

export function useCustomization(): CustomizationContextData {
  const context = useContext(CustomizationContext);

  if (!context) {
    throw new Error('useCustomization must be used within an CustomizationProvider');
  }

  return context;
}
